import React, { useState, useEffect } from 'react';
import {
  Row, Col, Form, Label, Input, Card, CardBody,
  Container, Button, FormFeedback, InputGroup
} from "reactstrap";

import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { fetchVehiclesCategoriesEditData } from "../../store/vehicles-categories/actions";
import "./CustomCheckbox.css";

const VehiclesCategoriesEdit = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(state => state.vehiclesCategories.edit);
  const navigate = useNavigate();

  const [typeId, setId] = useState(id);
  const [type_name, setTypeName] = useState('');
  const [display_name, setDisplayName] = useState('');
  const [display_name_ar, setDisplayNameAr] = useState('');
  const [seats, setSeats] = useState(4);
  const [vehicleType, setVehicleType] = useState("light");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [baseFare, setBaseFare] = useState('');
  const [stdFarePerKm, setStdFarePerKm] = useState('');
  const [timeRate, setTimeRate] = useState('');
  // Removed: waitingCharge, accept_min, addtionalCharges
  const [commissionCharges, setcommissionCharges] = useState('');
  const [vatCharges, setvatCharges] = useState('');

  const [checkboxes, setCheckboxes] = useState({
    is_active: false,
    is_towing: false,
    is_waste: false,
  });

  const [validation, setValidation] = useState({
    type_name: null,
    display_name: null,
    seats: null
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchVehiclesCategoriesEditData(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (data?.data) {
      const d = data.data;
      setId(d.id || id);
      setTypeName(d.vehicletype || "");
      setDisplayName(d.displayname || "");
      setDisplayNameAr(d.displayname_ar || "");
      setSeats(d.seats || 0);
      setVehicleType(d.is_heavy == 1 ? "heavy" : "light");
      setPreviewUrl(d.icon || null);

      const fares = d.fares || {};
      setBaseFare(fares.base_fare || "");
      setStdFarePerKm(fares.std_fare || "");
      setTimeRate(fares.time_rate || "");
  // Removed: waitingCharge, accept_min, addtionalCharges
      setcommissionCharges(fares.commission_charges || "");
      setvatCharges(fares.vat_charges || "");

      // checkboxes mapping
      setCheckboxes({
        is_active: d.isenable == 1,
        is_towing: d.is_towing == 1,
        is_waste: d.is_waste == 1,
      });
    }
  }, [data]);

  const handleCheckboxChange = (key) => {
    setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validateFields = () => {
    let isValid = true;
    const newValidation = {
      type_name: type_name.trim() !== '',
      display_name: display_name.trim() !== '',
      seats: Number(seats) > 0,
      baseFare: Number(baseFare) >= 0.01,
      stdFarePerKm: Number(stdFarePerKm) >= 0.01,
      timeRate: Number(timeRate) >= 0.01,
      commissionCharges: Number(commissionCharges) >= 0.01 && Number(commissionCharges) <= 100,
      vatCharges: Number(vatCharges) >= 0.01 && Number(vatCharges) <= 100,
    };

    setValidation(newValidation);

    Object.values(newValidation).forEach(v => {
      if (!v) isValid = false;
    });

    return isValid;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    const formData = new FormData();
    formData.append("id", typeId);
    formData.append("type_name", type_name);
    formData.append("display_name", display_name);
    formData.append("display_name_ar", display_name_ar);
    formData.append("seats", seats);
    formData.append("vehicle_type", vehicleType);
    formData.append("is_active", checkboxes.is_active ? 1 : 0);
    formData.append("is_towing", checkboxes.is_towing ? 1 : 0);
    formData.append("is_waste", checkboxes.is_waste ? 1 : 0);
    if (image) formData.append("image", image);

    formData.append("base_fare", baseFare);
    formData.append("fare_per_km", stdFarePerKm);
    formData.append("time_rate", timeRate);
    formData.append("commission_charges", commissionCharges);
    formData.append("vat_charges", vatCharges);

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/vehicles-categories/update`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        body: formData,
      });

      const resData = await response.json();

      if (response.status === 200) {
        toast.success('Vehicle category updated successfully', { autoClose: 3000 });
        navigate(`/vehicles-categories`);
      } else if (response.status === 422) {
        if (resData?.errors) {
          Object.values(resData.errors).forEach((errorArr) => {
            errorArr.forEach((errMsg) => toast.error(errMsg, { autoClose: 3000 }));
          });
        } else {
          toast.error(resData?.message || 'Validation failed', { autoClose: 3000 });
        }
      } else {
        toast.error(resData?.message || `Error: ${response.statusText}`, { autoClose: 3000 });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update vehicle category", { autoClose: 3000 });
    }
  };

  document.title = "Edit Vehicle Category | Moeen";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Home" breadcrumbItem="Edit Vehicle Category" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Form onSubmit={handleSubmit}>

                  {/* Type Name + Display Name */}
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <Label>Type Name</Label>
                        <Input
                          type="text"
                          placeholder="Enter type name"
                          value={type_name}
                          onChange={e => setTypeName(e.target.value)}
                          valid={validation.type_name === true}
                          invalid={validation.type_name === false}
                        />
                        <FormFeedback tooltip>Type name is required.</FormFeedback>
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="mb-3">
                        <Label>Display Name</Label>
                        <Input
                          type="text"
                          placeholder="Enter display name"
                          value={display_name}
                          onChange={e => setDisplayName(e.target.value)}
                          valid={validation.display_name === true}
                          invalid={validation.display_name === false}
                        />
                        <FormFeedback tooltip>Display name is required.</FormFeedback>
                      </div>
                    </Col>
                  </Row>

                  {/* Display Name Arabic */}
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <Label>Display Name (Arabic)</Label>
                        <Input
                          type="text"
                          placeholder="Enter display name in Arabic"
                          value={display_name_ar}
                          onChange={e => setDisplayNameAr(e.target.value)}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* Vehicle Type + Seats */}
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <Label>Vehicle Type</Label>
                        <div className="d-flex gap-3 vehicle-type-toggle">
                          {[{ label: "Light Vehicle", value: "light", icon: "🚗" },
                          { label: "Heavy Vehicle", value: "heavy", icon: "🚚" },
                          ].map(({ label, value, icon }) => (
                            <div
                              key={value}
                              className={`vehicle-type-option ${vehicleType === value ? "selected" : ""}`}
                              onClick={() => setVehicleType(value)}
                            >
                              <span className="icon">{icon}</span>
                              <span className="ms-2">{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="mb-3">
                        <Label>Seats / Weight</Label>
                        <InputGroup>
                          <Button type="button" color="primary" onClick={() => setSeats(seats - 1)}>
                            <i className="mdi mdi-minus" />
                          </Button>
                          <Input
                            type="number"
                            value={seats}
                            onChange={e => setSeats(Number(e.target.value))}
                            placeholder="Enter seats or weight"
                            valid={validation.seats === true}
                            invalid={validation.seats === false}
                          />
                          <Button type="button" color="primary" onClick={() => setSeats(seats + 1)}>
                            <i className="mdi mdi-plus" />
                          </Button>
                        </InputGroup>
                        <FormFeedback tooltip>This field is required.</FormFeedback>
                      </div>
                    </Col>
                  </Row>

                  {/* Image + Flags */}
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <Label>Upload Image</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        {previewUrl && (
                          <div className="mt-2">
                            <img
                              src={previewUrl}
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover"
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="mb-3">
                        <Label>Additional Flags</Label>
                        <div className="d-flex flex-wrap gap-3">
                          {[{ label: "Is Enabled", key: "is_active" },
                            { label: "Is Towing", key: "is_towing" },
                            { label: "Is Waste Disposal", key: "is_waste" }
                          ].map(({ label, key }) => (
                            <div
                              key={key}
                              className={`custom-box-checkbox ${checkboxes[key] ? "active" : ""}`}
                              onClick={() => handleCheckboxChange(key)}
                            >
                              {label}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Fare Inputs */}
                  <Row>
                    <Col md="4">
                      <div className="mb-3">
                        <Label>Base Fare</Label>
                        <Input
                          type="number"
                          placeholder="Enter base fare"
                          value={baseFare}
                          onChange={e => setBaseFare(e.target.value)}
                          min="0"
                          step="0.01"
                          valid={validation.baseFare === true}
                          invalid={validation.baseFare === false}
                        />
                        <FormFeedback tooltip>Base Fare min 0.01</FormFeedback>
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="mb-3">
                        <Label>Fare per KM</Label>
                        <Input
                          type="number"
                          placeholder="Enter fare per KM"
                          value={stdFarePerKm}
                          onChange={e => setStdFarePerKm(e.target.value)}
                          min="0"
                          step="0.01"
                          valid={validation.stdFarePerKm === true}
                          invalid={validation.stdFarePerKm === false}
                        />
                        <FormFeedback tooltip>Fare per KM 0.01</FormFeedback>
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="mb-3">
                        <Label>Time Rate (per min)</Label>
                        <Input
                          type="number"
                          placeholder="Enter time rate"
                          value={timeRate}
                          onChange={e => setTimeRate(e.target.value)}
                          min="0"
                          step="0.01"
                          valid={validation.timeRate === true}
                          invalid={validation.timeRate === false}
                        />
                        <FormFeedback tooltip>Time Rate (per min) 0.01</FormFeedback>
                      </div>
                    </Col>
                  </Row>

                  {/* Removed Waiting Charge, Accept Ride Min %, and Additional Charges inputs */}

                  <Row>
                    <Col md="4">
                      <div className="mb-3">
                        <Label>Commission Charges</Label>
                        <Input
                          type="number"
                          placeholder="Enter commission charges"
                          value={commissionCharges}
                          onChange={e => setcommissionCharges(e.target.value)}
                          min="0"
                          step="0.01"
                          valid={validation.commissionCharges === true}
                          invalid={validation.commissionCharges === false}
                        />
                        <FormFeedback tooltip>Commission Charges must be between 0.01% and 100%</FormFeedback>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="mb-3">
                        <Label>Vat Charges %</Label>
                        <Input
                          type="number"
                          placeholder="Enter vat charges"
                          value={vatCharges}
                          onChange={e => setvatCharges(e.target.value)}
                          min="0"
                          step="0.01"
                          valid={validation.vatCharges === true}
                          invalid={validation.vatCharges === false}
                        />
                        <FormFeedback tooltip>Vat Charges must be between 0.01% and 100%</FormFeedback>
                      </div>
                    </Col>
                  </Row>

                  {/* Submit */}
                  <Row className="mt-4">
                    <Col className="text-end">
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          "Submit"
                        )}
                      </Button>
                    </Col>
                  </Row>

                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VehiclesCategoriesEdit;
